import React from "react";
import Grid from "@material-ui/core/Grid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CustomText } from "../../../components/index";
import Styles from "../styles";
import { hooks, masks, validations } from "../../../utils";
import { api } from "../../../services";
import alerts from "../../../utils/alerts";
import { customModal } from "../../../components/modals/utils";
import ChechkInAndOut from "./ChechkInAndOut";
import { DrawMembers } from "./DrawMembers";
import {
  Input,
  InputDate,
  InputTags,
  InputPhone,
  Select,
} from "../../../components/inputs/newForm";

interface ExtraGuest {
  fullName: string;
  email: string;
}

interface GuestInfo {
  name?: string;
  cpf?: string;
  birthDate?: string;
  email?: string;
  phone?: string;
  id?: string | undefined | null;
  passport?: string;
  document?: string;
  vehiclePlate?: string;
}

interface FormSendData {
  id: string;
  role: string;
  property: string;
  numGuests: number;
  placeId: string;
  startDate: string;
  endDate: string;
  checkOutTime: string;
  checkInTime: string;
  fullName: string;
  passport?: string;
  document?: string;
  birthday: string;
  vehiclePlate?: string;
  email: string;
  phone: string;
  numberUsers: number;
  primaryGuest: GuestInfo;
  secondaryGuests: Partial<ExtraGuest>[];
  docType: number;
  docValue: string;
}

const normalBaseSchema = z.object({
  property: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .min(1, "*Campo Obrigatório"),
  startDate: z.date().nullable().optional(),
  endDate: z.date().nullable().optional(),
  checkInTime: z.string().optional(),
  checkOutTime: z.string().optional(),
  docType: z.union([z.literal(1), z.literal(2)]).optional(),
  docValue: z.string().optional(),
  fullName: z.string().optional(),
  birthday: z.date().nullable().optional(),
  email: z.string().optional(),
  vehiclePlate: z.string().optional(),
  phone: z.string().optional(),
  numberUsers: z.any().optional(),
});

type NormalFormData = z.infer<typeof normalBaseSchema>;

// Normalize a date string from server to a local Date object
function normalizeDate(val: any): Date | null {
  if (!val) return null;
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  }
  if (val instanceof Date) return val;
  return null;
}

export function GridRegisterNormal(props) {
  const { placeList, edit, oldData, reload, page, onImageUrlFetch } = props;
  const [show, setShow] = React.useState({
    name: false,
    doc: false,
    birthday: false,
    email: false,
    phone: false,
    plate: false,
    facial: false,
    renterPassword: false,
    reservationPassword: false,
    facialURL: false,
    checkS: false,
    members: false,
  });
  const [timeError, setTimeError] = React.useState<any>(false);
  const [docType, setDocType] = React.useState<any>(1);
  const [totalMembers, setTotalMembers] = React.useState(0);
  const [unError, setUnError] = React.useState<any>(false);

  const { loading, call } = hooks.useRequest();
  const [hourError, setHourError] = React.useState<any>(false);
  const [warning, setWarning] = React.useState<any>(false);

  const showRef = React.useRef(show);
  React.useEffect(() => { showRef.current = show; }, [show]);

  const normalSchema = React.useMemo(() =>
    normalBaseSchema.superRefine((data, ctx) => {
      const s = showRef.current;
      const req = (val: any, path: string) => {
        if (!val) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: "*Campo Obrigatório" });
      };
      if (s.checkS) {
        req(data.startDate, "startDate");
        req(data.endDate, "endDate");
        req(data.checkInTime, "checkInTime");
        req(data.checkOutTime, "checkOutTime");
      }
      if (s.doc) req(data.docValue, "docValue");
      if (s.name) req(data.fullName, "fullName");
      if (s.birthday) req(data.birthday, "birthday");
      if (s.email) req(data.email, "email");
      if (s.plate) req(data.vehiclePlate, "vehiclePlate");
      if (s.phone) req(data.phone, "phone");
    }), []);

  const { control, handleSubmit, setValue, getValues, watch, reset } =
    useForm<NormalFormData>({
      resolver: zodResolver(normalSchema),
      defaultValues: {
        property: oldData?.place?.id
          ? placeList?.find((p: any) => p.value === oldData.place.id)
            ? [placeList.find((p: any) => p.value === oldData.place.id)]
            : []
          : [],
        startDate: normalizeDate(oldData?.startDate),
        endDate: normalizeDate(oldData?.endDate),
        checkInTime: oldData ? masks.extractHour(oldData?.startDate) : "",
        checkOutTime: oldData ? masks.extractHour(oldData?.endDate) : "",
        docType: 1,
        docValue:
          oldData?.primaryGuest?.document ||
          oldData?.primaryGuest?.passport ||
          "",
        fullName: oldData?.primaryGuest?.name || "",
        birthday: normalizeDate(oldData?.primaryGuest?.birthDate),
        email: oldData?.primaryGuest?.email || "",
        vehiclePlate: oldData?.primaryGuest?.vehiclePlate || "",
        phone: oldData?.primaryGuest?.phone || "",
        numberUsers: 0,
      },
    });


  React.useEffect(() => {
    if (edit && oldData?.place?.id && placeList?.length > 0) {
      const found = placeList.find((p: any) => p.value === oldData.place.id);
      if (found) {
        setValue("property", [found]);
        showInputsHospede([found] as any);
      }
    }
  }, [placeList]);

  function showInputsHospede(
    placeID: [{ value: string; label: string }] | undefined
  ) {
    const paramMap: Record<string, keyof typeof show> = {
      GUEST_SHEET_NAME_ENABLED: "name",
      GUEST_SHEET_DOCUMENT_ENABLED: "doc",
      GUEST_SHEET_BIRTHDATE_ENABLED: "birthday",
      GUEST_SHEET_EMAIL_ENABLED: "email",
      GUEST_SHEET_PHONE_ENABLED: "phone",
      GUEST_SHEET_VEHICLE_PLATE_ENABLED: "plate",
      GUEST_SHEET_FACIAL_ENABLED: "facial",
      GUEST_SHEET_RENTER_PASSWORD_ENABLED: "renterPassword",
      GUEST_SHEET_RESERVATION_PASSWORD_ENABLED: "reservationPassword",
      GUEST_SHEET_FACIAL_URL_ENABLED: "facialURL",
    };

    const allTrue = {
      name: true, doc: true, birthday: true, email: true, phone: true,
      plate: true, facial: true, renterPassword: true,
      reservationPassword: true, facialURL: true, checkS: true, members: true,
    };

    if (!placeID) {
      setShow(allTrue);
      return;
    }

    const place = placeList?.find((p: any) => p.value === placeID[0].value);
    if (!place?.params) {
      setShow(allTrue);
      return;
    }

    const normalize = (value: any): boolean => {
      if (typeof value === "boolean") return value;
      const str = String(value ?? "").toLowerCase().trim();
      return str === "true" || str === "1";
    };

    const next = { ...allTrue };
    place.params.forEach((param: any) => {
      const key = paramMap[param.type as string];
      if (key) next[key] = normalize(param.value);
    });
    setShow(next);

    if (!oldData) {
      const getParamValue = (typeKey: string) => {
        const p = (place.params || []).find(
          (x: any) => String(x.type) === typeKey
        );
        return p ? String(p.value) : null;
      };

      const parseNum = (v: string | null, fallback: number) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
      };

      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

      const inHour = parseNum(getParamValue("CHECK_IN_DEFAULT_HOUR"), 14);
      const inMin = parseNum(getParamValue("CHECK_IN_DEFAULT_MINUTE"), 0);
      const outHour = parseNum(getParamValue("CHECK_OUT_DEFAULT_HOUR"), 12);
      const outMin = parseNum(getParamValue("CHECK_OUT_DEFAULT_MINUTE"), 0);

      setValue("checkInTime", `${pad(inHour)}:${pad(inMin)}`);
      setValue("checkOutTime", `${pad(outHour)}:${pad(outMin)}`);
    }
  }

  const findFirstValidFacial = (renters: any[]) => {
    if (!renters || renters.length === 0) return null;
    for (let i = renters.length - 1; i >= 0; i--) {
      if (renters[i].imageId) return renters[i];
    }
    alerts.alertError("Nenhum facial encontrado");
    return null;
  };

  const fetchImageById = (imageId: string) => {
    if (!imageId) return;
    try {
      call(null, api.getImageDownload(imageId), (response) => {
        if (response?.ok && response?.data) {
          onImageUrlFetch({ imageId, facialUrl: response.data });
        }
      });
    } catch (e) {
      console.log("failed to fetch image", e);
    }
  };

  const newReservation = async (data: NormalFormData) => {
    const submitDate: Partial<FormSendData> = {
      numGuests: data.numberUsers ? parseInt(data.numberUsers) : 0,
      placeId: data.property[0].value,
      startDate:
        masks.dateToSubmit(data.startDate) + " " + data?.checkInTime,
      endDate:
        masks.dateToSubmit(data.endDate) + " " + data?.checkOutTime,
      primaryGuest: {
        name: data.fullName,
        document:
          data.docType === 1
            ? masks.removeNumberMask(data.docValue)
            : undefined,
        passport: data.docType === 2 ? data.docValue : undefined,
        birthDate: data.birthday ? masks.dateToSubmit(data.birthday) : undefined,
        email: data.email,
        phone: data.phone,
        vehiclePlate: data.vehiclePlate
          ? masks.removePlacaMask(data.vehiclePlate)
          : undefined,
      },
      secondaryGuests: [],
    };

    const secondaryGuests: Partial<ExtraGuest>[] = [];
    const numUsers = parseInt(data.numberUsers) || 0;
    if (numUsers > 0) {
      for (let index = 1; index < numUsers + 1; index++) {
        const fn = (data as any)["fullName" + index];
        const em = (data as any)["email" + index];
        if (fn || em) {
          const newGuest: any = {};
          if (fn) newGuest["name"] = fn;
          if (em) newGuest["email"] = em;
          const ph = (data as any)["phone" + index];
          if (ph) newGuest["phone"] = ph;
          const bd = (data as any)["birthday" + index];
          if (bd) newGuest["birthDate"] = masks.dateToSubmit(bd);
          secondaryGuests.push(newGuest);
        }
      }
      submitDate.secondaryGuests = secondaryGuests;
    }

    if (edit) {
      delete submitDate.placeId;
      submitDate["id"] = oldData.id;
      submitDate.secondaryGuests = submitDate.secondaryGuests?.map(
        (i, index) => ({
          ...i,
          id: oldData?.secondaryGuests[index]?.id,
        })
      );
      submitDate.primaryGuest = {
        ...submitDate.primaryGuest,
        id: oldData?.primaryGuest?.id,
      };
      call(null, api.patchReservation(submitDate), async (response) => {
        if (response.ok) {
          customModal.close();
          reload(page);
          alerts.setErrorModal(
            "Reserva Editada",
            ["Reserva editada com sucesso!"],
            null,
            "Ok, Entendi",
            "#0C396D"
          );
        }
      });
    } else {
      call(null, api.postReservation(submitDate), async (response) => {
        if (response.ok) {
          customModal.close();
          reload(page);
          alerts.setErrorModal(
            "Reserva Cadastrada",
            ["Reserva cadastrada com sucesso!"],
            null,
            "Ok, Entendi",
            "#0C396D"
          );
        }
      });
    }
  };

  return (
    <>
      <Grid item xs={12} md={12} lg={3}>
        <InputTags
          control={control}
          name="property"
          label="Imóvel"
          readOnly={edit}
          options={placeList ?? []}
          onChangeCustom={(val) => {
            showInputsHospede(val);
          }}
        />
      </Grid>

      {show.checkS && (
        <ChechkInAndOut
          control={control}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          oldData={oldData}
          edit={edit}
        />
      )}

      {show.doc && (
        <Grid item xs={12} md={12} lg={3}>
          <Select
            control={control}
            name="docType"
            label="Tipo do Documento"
            defaultValue={1}
            onChangeCustom={(v) => {
              const numV = Number(v);
              setDocType(numV);
              setValue("docValue", "");
            }}
            options={[
              { value: 1, label: "CPF" },
              { value: 2, label: "Passaporte" },
            ]}
          />
        </Grid>
      )}

      {show.doc && (
        <Grid item xs={12} md={12} lg={3}>
          <Input
            control={control}
            name="docValue"
            label="Nº Documento"
            mask={docType === 1 ? masks.CPF : undefined}
            onBlur={() => {
              const docVal = getValues("docValue");
              const type = getValues("docType");
              if (type === 1 && docVal) {
                const cpf = masks.removeNumberMask(docVal);
                if (cpf.length === 11) {
                  call(null, api.validadeCPF(cpf), async (response) => {
                    if (response?.ok && response?.data?.length > 0) {
                      try {
                        setValue("fullName", response.data[0].name);
                        setValue("phone", response.data[0].phone);
                        setValue("email", response.data[0].email);
                        setValue("birthday", normalizeDate(response.data[0].birthDate));
                      } catch { }
                      const renterWithImage = findFirstValidFacial(response.data);
                      if (renterWithImage?.imageId) {
                        fetchImageById(renterWithImage.imageId);
                      }
                    }
                  });
                }
              }
            }}
          />
        </Grid>
      )}

      {show.name && (
        <Grid item xs={12} md={12} lg={3}>
          <Input
            control={control}
            name="fullName"
            label="Nome Completo"
          />
        </Grid>
      )}

      {show.birthday && (
        <Grid item xs={12} md={12} lg={3}>
          <InputDate
            control={control}
            maxDate={new Date()}
            name="birthday"
            label="Data de Nascimento"
          />
        </Grid>
      )}

      {show.email && (
        <Grid item xs={12} md={12} lg={3}>
          <Input
            control={control}
            name="email"
            label="E-Mail"
          />
        </Grid>
      )}

      {show.plate && (
        <Grid item xs={12} md={12} lg={3}>
          <Input
            control={control}
            name="vehiclePlate"
            label="Placa do Veículo"
          />
        </Grid>
      )}

      {show.phone && (
        <Grid item xs={12} md={12} lg={3}>
          <InputPhone
            control={control}
            name="phone"
            label="Telefone"
          />
        </Grid>
      )}

      {show.members && (
        <Grid item xs={12} md={12} lg={3}>
          <Input
            control={control}
            name="numberUsers"
            label="Nº Acompanhantes"
            type="number"
            mask={(value) => String(masks.onlyNumbers(value, 0, 10))}
            onBlur={() => {
              const val = getValues("numberUsers");
              setTotalMembers(masks.onlyNumbers(val, 0, 10));
            }}
          />
        </Grid>
      )}

      {DrawMembers(totalMembers, control)}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          paddingTop: "12px",
          width: "100%",
        }}
      >
        <CustomText
          style={{ whiteSpace: "pre-wrap" }}
          fontSize={1.75}
          textColor="#ff7733"
        >
          {unError && "*Campos Obrigatórios \n"}
          {timeError && timeError + "\n"}
          {hourError && "*" + hourError}
          {warning && "*" + warning}
        </CustomText>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Styles.CancelButton
            loading={loading}
            disabled={loading}
            onClick={() => customModal.close()}
            fullWidth={false}
          >
            Cancelar
          </Styles.CancelButton>
          <Styles.RegitryButton
            fullWidth={false}
            loading={loading}
            disabled={loading}
            onClick={() => {
              handleSubmit(
                (data) => { setUnError(false); newReservation(data); },
                () => setUnError(true)
              )();
            }}
          >
            {edit ? "Confirmar edição" : "Confirmar Cadastro"}
          </Styles.RegitryButton>
        </div>
      </div>
    </>
  );
}
